// App.js - Complete with ALL original functions + 4s Ad Screen
import React, { useEffect, useCallback, useState, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { BlogProvider } from './contexts/BlogContext';
import { fetchAppSettings } from './utils/appSettings';
import { PAYPAL_SDK_OPTIONS } from './utils/paypalSdkOptions';
import WebviewAuthHandoff from './components/WebviewAuthHandoff';

// Import AddToHomeScreenModal
import AddToHomeScreenModal from './components/AddToHomeScreenModal';
// Falling-envelope "letter from the devs" effect — shows unread admin
// letters targeted at the current user's segment (WeChat/email/etc).
import LetterDropEffect from './components/LetterDropEffect';

// Import notification and check-in services
import { useEnhancedCheckinLocationDetection } from './utils/enhancedCheckinLocationService';
import { useWaitlistNotifications } from './utils/waitlistNotificationService';
// Eager imports — needed for the very first screen (home page + nav)
import CheckinModal from './components/CheckinModal';
import Navigation from './components/Navigation';
import Home from './components/Home';
import ProtectedRoute from './components/ProtectedRoute';
import AdminMessageToast from './components/AdminMessageToast';
// Shared loader: same look for 'page code downloading' and 'page loading data'
import PageLoader from './components/PageLoader';
import { db, isWechatBrowser } from './firebase';
import { collection, query, where, onSnapshot, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import './App.css';
import AdScreenEnhanced from './components/AdScreenEnhanced';
import pushNotificationService from './utils/PushNotificationService';

// PERF: everything below is code-split with React.lazy. Previously every page
// (AdminDashboard 8k lines, TransactionAnalytics 5.5k, EventDetail 6.7k,
// Profile 4k, CourtQueue, CheckInPage, AnalyticsBuilder + recharts …) was in
// ONE bundle that every visitor had to download and parse before anything
// rendered. Now the first load only contains the home page; other pages load
// when visited (and the common ones are prefetched in the background once
// the home page is up — see prefetchCommonPages below).
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const PublicCheckInPage = lazy(() => import('./components/PublicCheckInPage'));
const WaitlistNotificationModal = lazy(() => import('./components/WaitlistNotificationModal'));
const MyEvents = lazy(() => import('./components/MyEvents'));
const CreateEvent = lazy(() => import('./components/CreateEvent'));
const Venues = lazy(() => import('./components/Venues'));
const Profile = lazy(() => import('./components/Profile'));
const EventDetail = lazy(() => import('./components/EventDetail'));
const PayRegistration = lazy(() => import('./components/PayRegistration'));
const Login = lazy(() => import('./components/Login'));
const WechatCallback = lazy(() => import('./components/WechatCallback'));
const Register = lazy(() => import('./components/Register'));
const InfoPage = lazy(() => import('./components/InfoPage'));
const BlogPostPage = lazy(() => import('./components/BlogPostPage'));
const CheckInPage = lazy(() => import('./components/CheckInPage'));
const CourtQueue = lazy(() => import('./components/CourtQueue'));
const AnalyticsBuilder = lazy(() => import('./components/AnalyticsBuilder'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const TransactionAnalytics = lazy(() => import('./components/TransactionAnalytics'));
const EmailTemplatePage = lazy(() => import('./components/EmailTemplatePage'));
const AdManager = lazy(() => import('./components/AdManager'));
const AppTutorial = lazy(() => import('./components/AppTutorial'));
const TUTORIAL_SEEN_KEY = 'pandaTutorialSeen_v2'; // keep in sync with AppTutorial.js

// Warm the browser cache with the pages people open most, once the home page
// is on screen and the main thread is idle. Clicking an event then feels
// instant even though it's no longer in the first bundle.
let commonPagesPrefetched = false;
function prefetchCommonPages() {
  if (commonPagesPrefetched) return;
  commonPagesPrefetched = true;
  const run = () => {
    import('./components/EventDetail').catch(() => {});
    import('./components/MyEvents').catch(() => {});
    import('./components/Profile').catch(() => {});
    import('./components/Login').catch(() => {});
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 4000 });
  else setTimeout(run, 2500);
}

// Rendered inside AuthProvider, so it only mounts once Firebase auth has
// resolved (AuthProvider renders nothing until then). For any route other
// than "/" it tells App the static loader can go — Home.js does the same via
// 'homeMounted' for "/". Without this, deep links like /event/123 kept the
// loader up for the full 8s fallback because Home never mounted.
function LoaderRelease() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/') {
      window.__appContentReady = true;
      window.dispatchEvent(new CustomEvent('appContentReady'));
    }
  }, [location.pathname]);
  return null;
}

// DELAYED PayPal imports - only loaded after auth is working
let PayPalScriptProvider = null;
let PaymentSuccess = null;
let PaymentCancelled = null;
let PayPalProvider = null;

// Enhanced PayPal loading with retry logic
const loadPayPalComponents = async (retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      //console.log(`Loading PayPal components (attempt ${attempt}/${retries})...`);
      
      const [
        paypalReact,
        paymentSuccessModule,
        paymentCancelledModule,
        paypalProviderModule
      ] = await Promise.all([
        import('@paypal/react-paypal-js'),
        import('./components/PaymentSuccess'),
        import('./components/PaymentCancelled'),
        import('./contexts/PayPalProvider')
      ]);
      
      PayPalScriptProvider = paypalReact.PayPalScriptProvider;
      PaymentSuccess = paymentSuccessModule.default;
      PaymentCancelled = paymentCancelledModule.default;
      PayPalProvider = paypalProviderModule.default;
      
      console.log('✅ PayPal components loaded successfully');
      return true;
    } catch (error) {
      console.warn(`⚠️ PayPal load attempt ${attempt} failed:`, error);
      if (attempt === retries) {
        console.error('❌ All PayPal load attempts failed');
        return false;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
  return false;
};

// Enhanced PayPal configuration with Safari optimizations
const getPayPalOptions = () => {
  if (!process.env.REACT_APP_PAYPAL_CLIENT_ID) return null;
  // Same object every provider uses — see utils/paypalSdkOptions.js.
  return PAYPAL_SDK_OPTIONS;
};

// Enhanced Service Worker Registration with auto-update
const registerServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    // Register Firebase messaging service worker (Firebase config passed via query string)
    const swUrl = '/firebase-messaging-sw.js?' + new URLSearchParams({
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
    }).toString();
    const messagingRegistration = await navigator.serviceWorker.register(swUrl, {
      scope: '/'
    });
    
    // Auto-update: when a new service worker is found, activate it immediately
    // so users don't stay on stale cached code after a deploy.
    messagingRegistration.addEventListener('updatefound', () => {
      const newWorker = messagingRegistration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated') {
            // Guard: only reload once per session to prevent loops.
            // Without this, clearing caches + reloading can cause the
            // fresh page to detect "another" update (race with cache
            // clearing), triggering an infinite reload cycle on mobile.
            if (sessionStorage.getItem('sw_reloaded')) return;

            // New SW activated — clear old caches and reload once
            if (caches) {
              caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))));
            }
            // Only auto-reload if user isn't mid-interaction
            if (!document.querySelector('input:focus, textarea:focus, [contenteditable]:focus')) {
              sessionStorage.setItem('sw_reloaded', 'true');
              window.location.reload();
            }
          }
        });
      }
    });

    // Check for updates every 30 minutes (catches deploys while tab is open)
    setInterval(() => { messagingRegistration.update().catch(() => {}); }, 30 * 60 * 1000);

    await navigator.serviceWorker.ready;
    
    // Initialize push notification service after service worker is ready
    setTimeout(() => {
      pushNotificationService.init();
    }, 1000);
    
    return true;
  } catch (error) {
    // SW registration failed — still works without it, just no push notifications
    return false;
  }
};

// Enhanced PayPal Error Boundary (complete version)
class KeepAliveBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error(`KeepAliveBoundary [${this.props.name}]:`, error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>Failed to load {this.props.name || 'this page'}.</p>
          <button onClick={() => this.setState({ hasError: false, retryCount: this.state.retryCount + 1 })}
            style={{ padding: '8px 20px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary:', error, errorInfo);
    // Keep the details in state, not just the console.
    //
    // This boundary is what produces the "the app just reloaded" symptom: any
    // uncaught error anywhere below it replaces the entire app with this
    // screen. Previously the screen said only "Something went wrong", so on a
    // phone — where there is no console to open — the actual cause was
    // unknowable and the failure looked like a random reload.
    this.setState({
      error,
      componentStack: errorInfo?.componentStack || null,
    });
  }
  render() {
    if (this.state.hasError) {
      const detail = [
        this.state.error?.message || String(this.state.error || 'Unknown error'),
        this.state.error?.stack || '',
        this.state.componentStack || '',
      ].filter(Boolean).join('\n\n');
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f9fafb' }}>
          <div style={{ maxWidth: 520, width: '100%', textAlign: 'center', background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,.08)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
              {this.state.error?.message || 'The page encountered an error.'}
            </p>

            {/* Try re-rendering before nuking the whole app. Most errors here
                come from one screen's state, and going back is usually enough
                to recover without losing everything else. */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => this.setState({ hasError: false, error: null, componentStack: null })}
                style={{ padding: '12px 20px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Try again
              </button>
              <button onClick={() => { window.location.href = '/'; }}
                style={{ padding: '12px 20px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Go home
              </button>
              <button onClick={() => {
                  try {
                    navigator.clipboard.writeText(detail);
                    alert('Error details copied — paste them when reporting this.');
                  } catch (e) {
                    this.setState({ showRaw: true });
                  }
                }}
                style={{ padding: '12px 20px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Copy details
              </button>
            </div>

            {this.state.showRaw && (
              <textarea readOnly value={detail}
                style={{ width: '100%', height: 180, marginTop: 14, fontSize: 11, fontFamily: 'monospace', padding: 8, border: '1px solid #e5e7eb', borderRadius: 8 }} />
            )}

            <details style={{ marginTop: 14, textAlign: 'left' }}>
              <summary style={{ fontSize: 12, color: '#9ca3af', cursor: 'pointer' }}>Technical details</summary>
              <pre style={{ fontSize: 10, color: '#6b7280', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                {detail}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

class PayPalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
   // console.error('PayPal Error Boundary caught an error:', error);
    //console.error('Error Info:', errorInfo);
    
    this.setState({
      error,
      errorInfo
    });
    
    if (error.message?.includes('authorization data')) {
     // console.error('PayPal Authorization Error: Vault functionality requires server-side token generation');
    }
    if (error.message?.includes('dataClientToken')) {
      //console.error('PayPal Token Error: Missing client token for vault operations');
    }
    if (error.message?.includes('SDK Validation error')) {
      //console.error('PayPal SDK Validation Error - Check configuration for production compatibility');
    }
  }

  render() {
    if (this.state.hasError) {
      const isAuthError = this.state.error?.message?.includes('authorization') ||
                         this.state.error?.message?.includes('dataClientToken') ||
                         this.state.error?.message?.includes('dataUserIdToken');
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isAuthError ? 'Payment Authorization Error' : 'Payment System Error'}
            </h2>
            <p className="text-gray-600 mb-4 text-sm">
              {isAuthError 
                ? 'Payment system is being updated for better security. Basic payments are still available.'
                : 'There was an issue loading the payment system. Please try refreshing the page.'
              }
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Enhanced notification and check-in provider (COMPLETE VERSION)
function AppNotificationProvider({ children }) {
  const { currentUser, isFirstLogin } = useAuth();
  const navigate = useNavigate();
  const [userEvents, setUserEvents] = React.useState([]);
  const [locationPermissionRequested, setLocationPermissionRequested] = React.useState(false);
  const [serviceWorkerReady, setServiceWorkerReady] = React.useState(false);
  const loginReturnHandled = React.useRef(false);

  // Redirect to saved return URL after login (especially for WeChat which loses React state)
  React.useEffect(() => {
    if (!currentUser || loginReturnHandled.current) return;
    const returnUrl = localStorage.getItem('loginReturnUrl');
    if (returnUrl) {
      loginReturnHandled.current = true;
      localStorage.removeItem('loginReturnUrl');
      // Small delay to let auth state settle
      setTimeout(() => navigate(returnUrl, { replace: true }), 300);
    }
  }, [currentUser]);

  // Enhanced check-in detection
  const {
    checkinOpportunity,
    isMonitoring,
    locationError,
    locationPermission,
    startMonitoring,
    stopMonitoring,
    updateEvents,
    dismissCheckinOpportunity,
    requestLocationPermission
  } = useEnhancedCheckinLocationDetection();

  // Waitlist notifications
  const {
    notifications: waitlistNotifications,
    hasUnread,
    markAsRead,
    clearNotification,
    clearAll
  } = useWaitlistNotifications(currentUser?.uid);

  // Show notification helper
  const showNotification = useCallback((message, type = 'info') => {
    // Shared toast container (top-center, stacked)
    let container = document.getElementById('app-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'app-toast-container';
      container.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:9999;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none;width:max-content;max-width:90vw;';
      document.body.appendChild(container);
    }

    // Inject the slide-in animation once
    if (!document.getElementById('app-toast-style')) {
      const style = document.createElement('style');
      style.id = 'app-toast-style';
      style.textContent = '@keyframes appToastIn{from{opacity:0;transform:translateY(-12px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}@keyframes appToastOut{to{opacity:0;transform:translateY(-8px) scale(.97)}}';
      document.head.appendChild(style);
    }

    // Dedupe: if the same message is already showing, just refresh its timer
    const existing = container.querySelector(`[data-msg="${CSS.escape(message)}"]`);
    if (existing) {
      clearTimeout(existing._dismissTimer);
      existing._dismissTimer = setTimeout(() => dismiss(existing), 4000);
      return;
    }

    const palette = {
      success: { bar: '#16a34a', icon: '#16a34a', bg: '#ffffff' },
      error:   { bar: '#dc2626', icon: '#dc2626', bg: '#ffffff' },
      warning: { bar: '#d97706', icon: '#d97706', bg: '#ffffff' },
      info:    { bar: '#2563eb', icon: '#2563eb', bg: '#ffffff' },
    };
    const c = palette[type] || palette.info;

    const icons = {
      success: 'M5 13l4 4L19 7',
      error: 'M6 18L18 6M6 6l12 12',
      warning: 'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
      info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    };

    const toast = document.createElement('div');
    toast.setAttribute('data-msg', message);
    toast.style.cssText = `pointer-events:auto;display:flex;align-items:center;gap:10px;background:${c.bg};color:#111827;box-shadow:0 8px 24px rgba(0,0,0,.12);border-radius:12px;padding:12px 16px;font-size:14px;font-weight:500;max-width:360px;animation:appToastIn .25s ease-out;`;
    toast.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${c.icon}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
        <path d="${icons[type] || icons.info}"></path>
      </svg>
      <span>${message}</span>
    `;

    const dismiss = (el) => {
      el.style.animation = 'appToastOut .2s ease-in forwards';
      setTimeout(() => { el.remove(); }, 200);
    };

    container.appendChild(toast);
    toast.addEventListener('click', () => dismiss(toast));
    toast._dismissTimer = setTimeout(() => dismiss(toast), 4000);
  }, []);

  // Handle successful check-in
  const handleCheckinSuccess = useCallback((event) => {
    showNotification(`Successfully checked in to ${event.title}!`, 'success');
    dismissCheckinOpportunity();
  }, [dismissCheckinOpportunity, showNotification]);

  // Initialize service workers on app load
  useEffect(() => {
    const initializeServiceWorkers = async () => {
      //console.log('Initializing service workers...');
      const success = await registerServiceWorkers();
      setServiceWorkerReady(success);
      
      if (success) {
        // service workers ready
      } else if (!isWechatBrowser()) {
        // 微信里没有 service worker / web push 是正常的，不算功能降级，不要吓用户
        showNotification('Some features may be limited', 'warning');
      }
    };

    initializeServiceWorkers();
  }, [showNotification]);

  // COMPLETE user events registration listener
  useEffect(() => {
    if (!currentUser) {
      stopMonitoring();
      setUserEvents([]);
      return;
    }

    let updateTimeout;
    let isUpdating = false;
    
    const registrationsQuery = query(
      collection(db, 'registrations'),
      where('userId', '==', currentUser.uid),
      where('status', '!=', 'cancelled'),
      orderBy('status'),
      orderBy('registeredAt', 'desc')
    );

    const unsubscribeRegistrations = onSnapshot(registrationsQuery, async (snapshot) => {
      if (isUpdating) {
       // console.log('App: Update already in progress, skipping');
        return;
      }

      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }

      const hasLocationRelevantChanges = snapshot.docChanges().some(change => {
        if (change.type === "added" || change.type === "removed") {
          return true;
        }
        if (change.type === "modified") {
          const newData = change.doc.data();
          const locationRelevantFields = ['status', 'checkedIn'];
          
          const existingEvent = userEvents.find(e => e.userRegistration?.id === change.doc.id);
          if (!existingEvent) return true;
          
          return locationRelevantFields.some(field => 
            existingEvent.userRegistration[field] !== newData[field]
          );
        }
        return false;
      });

      if (!hasLocationRelevantChanges && userEvents.length > 0) {
       // console.log('App: Skipping update - no location-relevant changes');
        return;
      }

      updateTimeout = setTimeout(async () => {
        if (isUpdating) return;
        isUpdating = true;

        try {
          const registrations = [];
          snapshot.forEach(doc => {
            registrations.push({ id: doc.id, ...doc.data() });
          });

          const eventIds = [...new Set(registrations.map(reg => reg.eventId))];
          const existingEventIds = userEvents.map(e => e.id);
          const needsEventRefresh = !eventIds.every(id => existingEventIds.includes(id)) || 
                                   eventIds.length !== existingEventIds.length;

          let eventsWithRegistration = [...userEvents];

          if (needsEventRefresh) {
            eventsWithRegistration = [];
            
            if (eventIds.length > 0) {
              const batches = [];
              const eventIdsCopy = [...eventIds];
              while (eventIdsCopy.length) {
                const batch = eventIdsCopy.splice(0, 10);
                batches.push(batch);
              }

              for (const batch of batches) {
                try {
                  const eventsQuery = query(
                    collection(db, 'events'),
                    where('__name__', 'in', batch)
                  );
                  
                  const eventsSnapshot = await getDocs(eventsQuery);
                  
                  eventsSnapshot.forEach(eventDoc => {
                    const eventData = { id: eventDoc.id, ...eventDoc.data() };
                    const userRegistration = registrations.find(reg => reg.eventId === eventData.id);
                    
                    if (userRegistration) {
                      eventData.userRegistration = userRegistration;
                      eventsWithRegistration.push(eventData);
                    }
                  });
                } catch (error) {
                 // console.error('Error fetching events batch:', error);
                }
              }
            }
          } else {
            eventsWithRegistration = userEvents.map(event => {
              const userRegistration = registrations.find(reg => reg.eventId === event.id);
              return userRegistration ? { ...event, userRegistration } : event;
            });
          }

          if (JSON.stringify(eventsWithRegistration) !== JSON.stringify(userEvents)) {
            setUserEvents(eventsWithRegistration);
            updateEvents(eventsWithRegistration);
          }
          
        } catch (error) {
         // console.error('Error updating user events:', error);
        } finally {
          isUpdating = false;
        }
      }, 2000);
    });

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      isUpdating = false;
      unsubscribeRegistrations();
    };
  }, [currentUser]);

  // Location monitoring effect
  useEffect(() => {
    if (userEvents.length === 0) {
      stopMonitoring();
      return;
    }

    const now = new Date();
    const upcomingEvents = userEvents.filter(event => {
      const eventStart = new Date(event.startDate);
      const minutesUntilStart = (eventStart - now) / (1000 * 60);
      return minutesUntilStart <= 30 && minutesUntilStart >= 0;
    });

    if (upcomingEvents.length > 0 && !isMonitoring) {
      //console.log(`Starting location monitoring for ${upcomingEvents.length} upcoming events`);
      startMonitoring(userEvents);
    } else if (upcomingEvents.length === 0 && isMonitoring) {
     // console.log('No upcoming events, stopping location monitoring');
      stopMonitoring();
    }
  }, [userEvents.length, isMonitoring]);
  
  // Location permission request effect
  useEffect(() => {
    if (userEvents.length > 0 && 
        locationPermission === 'prompt' && 
        !locationPermissionRequested) {
      
        const now = new Date();
        const hasUpcomingEvents = userEvents.some(event => {
            const eventStart = new Date(event.startDate);
            const minutesUntilStart = (eventStart - now) / (1000 * 60);
            return minutesUntilStart <= 30 && minutesUntilStart >= 0;
        });

        if (hasUpcomingEvents) {
            setLocationPermissionRequested(true);
            showLocationPermissionDialog();
        }
    }
  }, [userEvents, locationPermission, locationPermissionRequested]);

  // COMPLETE location permission dialog
  const showLocationPermissionDialog = useCallback(() => {
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4';
    dialog.innerHTML = `
      <div class="bg-white rounded-xl max-w-md w-full mx-4 p-6">
        <div class="text-center">
          <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Enable Location for Check-in</h3>
          <p class="text-gray-600 text-sm mb-6">Allow location access to automatically detect when you're at event venues and enable quick check-in.</p>
          <div class="flex gap-3">
            <button id="locationDeny" class="flex-1 px-4 py-2 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50">
              Not Now
            </button>
            <button id="locationAllow" class="flex-1 px-4 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white">
              Enable Location
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);

    dialog.querySelector('#locationAllow').addEventListener('click', async () => {
      const granted = await requestLocationPermission();
      if (granted) {
        showNotification('Location access enabled! Check-in will be available when you\'re near event venues.', 'success');
      } else {
        showNotification('Location access denied. You can enable it later in your browser settings.', 'warning');
      }
      dialog.remove();
    });

    dialog.querySelector('#locationDeny').addEventListener('click', () => {
      showNotification('Location access not enabled. Check-in features will not be available.', 'info');
      dialog.remove();
    });

    setTimeout(() => {
      if (document.body.contains(dialog)) {
        dialog.remove();
      }
    }, 30000);
  }, [requestLocationPermission, showNotification]);

  // Location error notifications
  useEffect(() => {
    if (locationError) {
      showNotification(locationError, 'error');
    }
  }, [locationError, showNotification]);

  // Notification badge updates
  useEffect(() => {
   // console.log('Notification state changed:', { hasUnread, count: waitlistNotifications.length });
    
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        hasUnread, 
        count: waitlistNotifications.filter(n => !n.read && !n.expired && n.urgent).length 
      }
    }));
  }, [hasUnread, waitlistNotifications]);

  return (
    <>
      {children}
      
      <CheckinModal
        checkinOpportunity={checkinOpportunity}
        isOpen={!!checkinOpportunity}
        onClose={dismissCheckinOpportunity}
        onCheckinSuccess={handleCheckinSuccess}
      />

      {/* Lazy + only mounted when there is something to show: this modal
          imports PayPalPayment (and the PayPal SDK wrapper), which used to sit
          in the first-load bundle for every visitor. */}
      {waitlistNotifications.length > 0 && (
        <Suspense fallback={null}>
          <WaitlistNotificationModal
            notifications={waitlistNotifications}
            onMarkAsRead={markAsRead}
            onClearNotification={clearNotification}
          />
        </Suspense>
      )}

      {/* Admin-only: toast for new / unread player chat messages */}
      <AdminMessageToast />
      
      {/* Player invite notifications — slides in from top when someone invites you */}
      <PlayerInviteBanner />

      {/* Add-to-home-screen doesn't work inside the WeChat in-app browser, so hide it there */}
      {!isWechatBrowser() && <AddToHomeScreenModal isFirstLogin={isFirstLogin} />}

      {/* Falling envelope for any unread admin letters targeted at this user */}
      <LetterDropEffect />
    </>
  );
}

// ── Player Invite Banner ──
// Listens for unread player_invite notifications and shows a slide-down
// banner at the top of the screen with sender info + "Go to Event" button.
function PlayerInviteBanner() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = React.useState([]);

  React.useEffect(() => {
    if (!currentUser) { setInvites([]); return; }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid),
      where('type', '==', 'player_invite'),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setInvites(items);
    }, (err) => {
      console.error('[PlayerInviteBanner] listener error:', err);
    });

    return () => unsub();
  }, [currentUser]);

  const dismiss = async (inviteId) => {
    try {
      await updateDoc(doc(db, 'notifications', inviteId), { read: true });
    } catch (err) {
      console.error('[PlayerInviteBanner] dismiss error:', err);
    }
    setInvites(prev => prev.filter(i => i.id !== inviteId));
  };

  const goToEvent = async (inv) => {
    await dismiss(inv.id);
    navigate(`/event/${inv.eventId}`);
  };

  if (invites.length === 0) return null;

  return (
    <div style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 9998, width: '92%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {invites.slice(0, 3).map((inv) => (
        <div
          key={inv.id}
          style={{
            background: '#ffffff',
            borderRadius: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            animation: 'playerInviteSlideIn 0.35s ease-out',
            border: '1px solid rgba(168,85,247,0.2)',
          }}
        >
          {inv.senderAvatar ? (
            <img src={inv.senderAvatar} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #e9d5ff' }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
              {(inv.senderName || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
              <span style={{ fontWeight: 700, color: '#7c3aed' }}>{inv.senderName || 'Someone'}</span>
              <span> invited you to</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              🏸 {inv.eventTitle || 'an event'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                onClick={() => goToEvent(inv)}
                style={{
                  flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 10,
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)', color: '#fff',
                  fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                Go to Event →
              </button>
              <button
                onClick={() => dismiss(inv.id)}
                style={{
                  padding: '7px 14px', borderRadius: 10,
                  background: '#f3f4f6', color: '#6b7280', border: 'none',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Later
              </button>
            </div>
          </div>
          <button
            onClick={() => dismiss(inv.id)}
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 2, fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: -2 }}
          >
            ×
          </button>
        </div>
      ))}
      <style>{`
        @keyframes playerInviteSlideIn {
          from { opacity: 0; transform: translateY(-20px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function App() {
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalOptions, setPaypalOptions] = useState(null);
  const [showAd, setShowAd] = useState(true);

  // Loading screen hides once Home.js has MOUNTED (its skeleton is already
  // painted on screen at that point — see the 'homeMounted' dispatch in
  // Home.js). This used to also wait on the ad screen being ready/skipped,
  // but with ads currently disabled that bought nothing — AdScreenEnhanced
  // still runs and overlays independently if ads are ever turned back on,
  // it just no longer gates this loader.
  // This is intentionally NOT gated on 'homeDataReady' (which only fires
  // once real Firestore data arrives) — waiting for real data would mean
  // keeping the static loader up for however long Firestore takes, which is
  // exactly the slow/blank gap we're trying to avoid. Home's skeleton IS
  // real visible content, so the moment it's mounted, there's nothing left
  // for the static loader to be covering up.
  //
  // MIN_DISPLAY_MS below holds the loader up for at least this long even if
  // Home mounts sooner — purely a deliberate/branded pacing choice,
  // not a correctness requirement. This is implemented as a plain delay
  // before calling hideLoadingScreen(), which still does an instant
  // snap-hide (display:none, no transition) — so this can't reintroduce the
  // earlier bug where a CSS fade left a blank-peach frame mid-transition.
  const MIN_DISPLAY_MS = 1200;

  const homeReadyRef = useRef(false);
  const screenHiddenRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  const minTimeTimerRef = useRef(null);

  const hideLoadingScreen = useCallback(() => {
    if (screenHiddenRef.current) return;
    screenHiddenRef.current = true;
    if (minTimeTimerRef.current) {
      clearTimeout(minTimeTimerRef.current);
      minTimeTimerRef.current = null;
    }
    requestAnimationFrame(() => {
      const screen = document.getElementById('loading-screen');
      if (screen) {
        // Snap-hide instantly — no fade. A previous opacity+visibility
        // transition left a ~0.5s window where the logo/GIF had already
        // faded toward transparent but the peach background hadn't, so the
        // screen briefly looked like a blank peach rectangle with no
        // content — exactly the "pure pink" gap that was being reported.
        // Setting display:none directly removes it from paint in one
        // frame, with nothing left to half-render underneath.
        screen.style.transition = 'none';
        screen.style.display = 'none';
      }
      sessionStorage.setItem('appLoaded', 'true');
    });
  }, []);

  const maybeHideLoadingScreen = useCallback(() => {
    if (!homeReadyRef.current) return;

    const elapsed = Date.now() - mountTimeRef.current;
    const remaining = MIN_DISPLAY_MS - elapsed;

    if (remaining <= 0) {
      hideLoadingScreen();
    } else if (!minTimeTimerRef.current) {
      // Home is ready, but the minimum hasn't elapsed yet — wait out the
      // remainder, then snap-hide. No CSS transition involved here either,
      // just a delayed call to the same instant hide.
      minTimeTimerRef.current = setTimeout(() => {
        minTimeTimerRef.current = null;
        hideLoadingScreen();
      }, remaining);
    }
  }, [hideLoadingScreen]);

  // Called by AdScreenEnhanced once ads are loaded/skipped/disabled. No
  // longer feeds into the loading-screen gate — the loader now depends only
  // on Home mounting (see comment above maybeHideLoadingScreen). Kept as a
  // no-op since AdScreenEnhanced still calls it as part of its own
  // lifecycle; safe to leave wired up.
  const handleReady = useCallback(() => {}, []);

  // Cold start: index.html loading screen is already showing.
  //
  // WHITE-SCREEN FIX: this used to hide the loader immediately on any reload
  // within the same session (sessionStorage 'appLoaded'). But at that moment
  // AuthProvider is still rendering NOTHING while Firebase auth resolves, so
  // every refresh showed a blank page. Now a revisit just skips the branded
  // minimum time; the loader still stays until real content has mounted.
  useEffect(() => {
    if (sessionStorage.getItem('appLoaded')) {
      mountTimeRef.current = Date.now() - MIN_DISPLAY_MS;
    }

    // Covers the rare case where Home dispatched 'homeMounted' before this
    // effect's listener was attached (component mount order isn't
    // guaranteed) — check the plain window flags Home / LoaderRelease set.
    if (window.__homeMounted || window.__appContentReady) {
      homeReadyRef.current = true;
      prefetchCommonPages();
    }

    // Home.js dispatches 'homeMounted' the moment it first mounts (skeleton
    // showing); LoaderRelease dispatches 'appContentReady' for every other
    // route once auth has resolved.
    const onHomeMounted = () => {
      homeReadyRef.current = true;
      maybeHideLoadingScreen();
      prefetchCommonPages();
    };
    window.addEventListener('homeMounted', onHomeMounted);
    window.addEventListener('appContentReady', onHomeMounted);
    maybeHideLoadingScreen();

    // Absolute backstop — never let the static loader hang past this,
    // regardless of Home's state (covers Home failing to mount at all,
    // a JS error before render, etc). Independent of MIN_DISPLAY_MS — if
    // things are genuinely broken, bail out at 8s rather than waiting on
    // a minimum that's never going to matter.
    const fallback = setTimeout(() => {
      hideLoadingScreen();
    }, 8000);

    return () => {
      window.removeEventListener('homeMounted', onHomeMounted);
      window.removeEventListener('appContentReady', onHomeMounted);
      if (minTimeTimerRef.current) clearTimeout(minTimeTimerRef.current);
      clearTimeout(fallback);
    };
  }, [maybeHideLoadingScreen, hideLoadingScreen]);

  const handleAdComplete = () => {
    setShowAd(false);
  };

  // COMPLETE PayPal configuration validation
  React.useEffect(() => {
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    
    if (!clientId) {
      //console.error('PayPal Client ID is not configured. Please add REACT_APP_PAYPAL_CLIENT_ID to your environment variables.');
      return;
    }
    
    const isSandboxClientId = clientId.includes('sb-');
    
    if (isSandboxClientId) {
      //console.warn('WARNING: Using sandbox PayPal Client ID in production environment. Please update to production client ID.');
    } else {
     // console.log('✅ Using production PayPal Client ID');
    }
    
    //console.log(`PayPal Configuration:
   //   Environment: production
    //  Client ID Type: ${isSandboxClientId ? 'Sandbox (WARNING!)' : 'Production'}
    ////  Client ID: ${clientId.substring(0, 10)}...
     // Vault: Disabled (to avoid auth errors)`);
  }, []);

  // Load PayPal AFTER authentication is working (with enhanced retry)
  useEffect(() => {
    const initializePayPal = async () => {
     // console.log('Checking PayPal configuration...');
      
      const options = getPayPalOptions();
      if (!options) {
     //   console.warn('PayPal not configured, skipping PayPal initialization');
        setPaypalReady(false);
        return;
      }

    //  console.log('Loading PayPal components with retry logic...');
      const success = await loadPayPalComponents();
      
      if (success) {
        setPaypalOptions(options);
        setPaypalReady(true);
      //  console.log('✅ PayPal initialization complete');
      } else {
      //  console.warn('⚠️ PayPal initialization failed, continuing without payment features');
        setPaypalReady(false);
      }
    };

    // Delay PayPal initialization to let auth load first
    // Delay PayPal initialization slightly to let auth start first.
    // Shortened from the original 3000ms — this no longer risks remounting
    // Home when paypalReady flips (see the wrapper-depth fix in MainApp
    // below), so there's no reason to wait long for it.
    const timer = setTimeout(initializePayPal, 1200);
    
    return () => clearTimeout(timer);
  }, []);

  // Always render the main app so Home can load in background.
  // AdScreenEnhanced overlays on top (z-index 10000) until dismissed.
  const mainApp = (
    <Router>
      <AuthProvider>
        {/* Consumes ?authToken= from WeChat Mini Program handoffs and
            signs the user in silently. Renders nothing. Must be inside
            AuthProvider so onAuthStateChanged fires afterward. */}
        <WebviewAuthHandoff />
        <LoaderRelease />
        <LanguageProvider>
        <BlogProvider>
          <div className="min-h-screen bg-gray-50">
            <Suspense fallback={<PageLoader fullScreen />}>
            <Routes>
              {/* Public routes that don't require PayPal */}
              <Route path="/login" element={<Login />} />
              <Route path="/wechat-callback" element={<WechatCallback />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              
              {/* Admin Check-in page - requires admin auth */}
              <Route path="/check-in/:eventId" element={
                <ProtectedRoute adminOnly>
                  <CheckInPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/analytics" element={
                <ProtectedRoute adminOnly>
                  <TransactionAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/admin/email-templates" element={
                <ProtectedRoute adminOnly>
                  <EmailTemplatePage />
                </ProtectedRoute>
              } />
              <Route path="/admin/ads" element={
                <ProtectedRoute adminOnly>
                  <AdManager />
                </ProtectedRoute>
              } />

               <Route path="/admin/charts" element={
                <ProtectedRoute adminOnly>
                  <AnalyticsBuilder />
                </ProtectedRoute>
              } />


              {/* Public Check-in page - accessible via QR code, no auth required */}
              <Route path="/public-checkin/:eventId" element={<PublicCheckInPage />} />

              {/* PayPal payment callback routes */}
              {paypalReady && PaymentSuccess && PaymentCancelled && (
                <>
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                </>
              )}
              
              {/* All other routes */}
              <Route path="/*" element={<MainApp paypalReady={paypalReady} paypalOptions={paypalOptions} />} />
            </Routes>
            </Suspense>
          </div>
        </BlogProvider>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  );

  return (
    <>
      {mainApp}
      {showAd && <AdScreenEnhanced onComplete={handleAdComplete} onReady={handleReady} />}
    </>
  );
}

// Shows the animated tutorial once to each signed-in user (per device), a
// moment after the app has settled. Skipped during first-login profile setup
// and on pages where it would get in the way.
function TutorialGate() {
  const { currentUser, isFirstLogin } = useAuth();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [cfg, setCfg] = useState(null);

  // Live tutorial switch, like letters: a realtime listener on appConfig/app,
  // so turning it on reaches players who already have the site open. If the
  // Firestore rules don't let players read that doc, fall back to asking the
  // server every minute and whenever the tab comes back into view.
  useEffect(() => {
    if (!currentUser) return undefined;
    let alive = true;
    let timer = null;
    const poll = () => fetchAppSettings({ fresh: true }).then((v) => { if (alive) setCfg(v); });
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    const startPolling = () => {
      poll();
      timer = setInterval(poll, 60000);
      document.addEventListener('visibilitychange', onVisible);
    };
    let unsub = null;
    try {
      unsub = onSnapshot(doc(db, 'appConfig', 'app'), (snap) => {
        const d = snap.exists() ? snap.data() : {};
        if (alive) setCfg({ tutorialEnabled: d.tutorialEnabled === true, tutorialVersion: Number(d.tutorialVersion) || 0 });
      }, () => { if (alive && !timer) startPolling(); });
    } catch (_) { startPolling(); }
    return () => {
      alive = false;
      if (unsub) unsub();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || isFirstLogin || !cfg || !cfg.tutorialEnabled || show) return undefined;
    // localStorage holds the tutorial version this device last saw ("1" =
    // seen before versions existed). Turning it on / "show again" publishes
    // a newer version → everyone sees it once.
    let seenVersion = Infinity;
    try {
      const raw = localStorage.getItem(TUTORIAL_SEEN_KEY);
      seenVersion = raw ? (Number(raw) || 1) : 0;
    } catch (_) { /* storage blocked → never auto-show */ }
    if (!(Number(cfg.tutorialVersion || 1) > seenVersion)) return undefined;
    // Not over pages where it would get in the way.
    const p = location.pathname;
    if (/^\/(tutorial|admin|check-in|public-checkin|pay|payment)/.test(p) || /\/courts$/.test(p)) return undefined;
    const t = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(t);
  }, [currentUser, isFirstLogin, cfg, location.pathname, show]);

  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <AppTutorial overlay onClose={() => setShow(false)} />
    </Suspense>
  );
}

function MainApp({ paypalReady, paypalOptions }) {
  const { currentUser, isAdmin } = useAuth();
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  const isProfileRoute = location.pathname === '/profile';

  // Lazy keep-alive: mount only after first visit, then keep alive forever.
  // This avoids loading heavy components in the background on mobile app start.
  const [adminVisited, setAdminVisited] = useState(false);
  const [profileVisited, setProfileVisited] = useState(false);
  useEffect(() => {
    if (isAdminRoute && isAdmin && currentUser) setAdminVisited(true);
  }, [isAdminRoute, isAdmin, currentUser]);
  useEffect(() => {
    if (isProfileRoute && currentUser) setProfileVisited(true);
  }, [isProfileRoute, currentUser]);

  const routes = (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 pb-16 md:pb-0">
        {/* AdminDashboard: mounted on first /admin visit, then kept alive */}
        {adminVisited && isAdmin && currentUser && (
          <div style={{ display: isAdminRoute ? 'block' : 'none' }}>
            <KeepAliveBoundary name="Admin Dashboard">
              <Suspense fallback={<PageLoader />}>
                <AdminDashboard />
              </Suspense>
            </KeepAliveBoundary>
          </div>
        )}
        {/* Profile: mounted on first /profile visit, then kept alive */}
        {profileVisited && currentUser && (
          <div style={{ display: isProfileRoute ? 'block' : 'none' }}>
            <KeepAliveBoundary name="Profile">
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            </KeepAliveBoundary>
          </div>
        )}
        <div style={{ display: (isAdminRoute && adminVisited && isAdmin) || (isProfileRoute && profileVisited && currentUser) ? 'none' : 'block' }}>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/info" element={<InfoPage />} />
          <Route path="/blog/:id" element={<BlogPostPage />} />
          <Route path="/tutorial" element={<AppTutorial overlay />} />
            <Route path="/venues" element={<Venues />} />
            <Route path="/event/:id" element={<EventDetail />} />
            <Route path="/event/:id/courts" element={<ProtectedRoute><CourtQueue /></ProtectedRoute>} />
            <Route path="/pay/:registrationId" element={<ProtectedRoute><PayRegistration /></ProtectedRoute>} />
            
            {/* Protected routes */}
            <Route path="/my-events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><div /></ProtectedRoute>} />
            
            {/* Admin routes */}
            <Route path="/create-event" element={<ProtectedRoute adminOnly><CreateEvent /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><div /></ProtectedRoute>} />

          </Routes>
        </Suspense>
        </div>
        </div>
        <Navigation />
        <TutorialGate />
      </div>
  );

  // IMPORTANT: this tree shape must stay EXACTLY the same regardless of
  // paypalReady — same component slots, same nesting depth, every render.
  // Only WHICH component fills the PayPal slots changes (real provider vs.
  // a plain passthrough), never whether the slots exist at all.
  //
  // Previously, when paypalReady flipped from false -> true, the JSX went
  // from "routes rendered directly under AppNotificationProvider" to
  // "routes wrapped in 3 new provider layers" — a change in tree depth at
  // that position. React can't reconcile that as a prop update; it tears
  // down the entire old subtree and mounts a fresh one. Since Home lives
  // inside `routes`, that meant Home (and its Firestore listener, and its
  // loading skeleton) was being unmounted and remounted from scratch a few
  // seconds after first paint, every single time — the "duplicate loading"
  // behaviour. AppNotificationProvider's own effects (service worker,
  // notification listeners) were also at risk of re-running for the same
  // reason, which is what the original comment about the duplicated
  // "features limited" toast was about — same root cause, two symptoms.
  //
  // PayPalReadyGate below is the fix: a single, always-present component
  // that internally decides whether to render the real PayPal providers or
  // just pass its children through — so the tree shape never changes, only
  // the gate's internal behaviour does.
  return (
    <AppNotificationProvider>
      <AppErrorBoundary>
      <PayPalErrorBoundary>
        <PayPalReadyGate paypalReady={paypalReady} paypalOptions={paypalOptions}>
          {routes}
        </PayPalReadyGate>
      </PayPalErrorBoundary>
      </AppErrorBoundary>
    </AppNotificationProvider>
  );
}

 
function PayPalReadyGate({ paypalReady, paypalOptions, children }) {
  if (paypalReady && PayPalScriptProvider && PayPalProvider && paypalOptions) {
    return (
      <PayPalScriptProvider options={paypalOptions} deferLoading={false}>
        <PayPalProvider>
          {children}
        </PayPalProvider>
      </PayPalScriptProvider>
    );
  }
  return children;
}

export default App;